/*
 * DISTRIBUTION STATEMENT C. Distribution authorized to U.S. Government Agencies and their contractors; Critical
 * Technology; 19 October 2017. Other requests for this document shall be referred to Chemical and Biological
 * Technologies, Defense Threat Reduction Agency, 8725 John J Kingman Road, Fort Belvoir, VA 22060-6201.
 *
 * WARNING: This document contains technical data whose export is restricted by the Arms Export Control Act (Sec 2751
 * of Title 22, United States Code) or the Export Control Reform Act of 2018 (Chapter 58 Sections 4801-4852 of Title
 * 50, United States Code). Violations of these export laws are subject to severe criminal penalties. Disseminate in
 * accordance with provisions of DoD Directive 5230.25 and DoD Instruction 2040.02. DESTRUCTION NOTICE: For
 * unclassified, limited distribution documents, destroy by any method that will prevent disclosure of the contents
 * or reconstruction of the document.
 *
 * This material is based upon work supported by the Under Secretary of War for Research and Engineering under Air
 * Force Contract No. FA8702-15-D-0001 or FA8702-25-D-B002. Any opinions, findings, conclusions or recommendations
 * expressed in this material are those of the author(s) and do not necessarily reflect the views of the Under
 * Secretary of War for Research and Engineering.
 *
 * © 2026 Massachusetts Institute of Technology.
 *
 * The software/firmware is provided to you on an As-Is basis
 *
 * Delivered to the U.S. Government with Unlimited Rights, as defined in DFARS Part 252.227-7013 or 7014 (Feb 2014).
 * Notwithstanding any copyright notice, U.S. Government rights in this work are defined by DFARS 252.227-7013 or
 * DFARS 252.227-7014 as detailed above. Use of this work other than as specifically authorized by the U.S.
 * Government may violate any copyrights that exist in this work.
 *
 * This document contains Controlled Unclassified Information (CUI). Handling of this information is subject to the
 * controls identified in DFARS 252.204-7012 - SAFEGUARDING COVERED DEFENSE INFORMATION AND CYBER INCIDENT REPORTING.
 */

import { connect } from 'react-redux'

// React Router
import {
  BrowserRouter as Router,
  Link as LinkRouter,
  useNavigate
} from 'react-router-dom';

// Material-UI
import { makeStyles } from 'tss-react/mui';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';

import {
  Button
} from 'rapids-gui-library';

// App
import { classificationStyles } from "../styles/common";
import CuiBanner from "../components/CuiBanner";
import backgroundImage from '../assets/images/background-footer.png';

// App logos
import appLogo from '../assets/images/app_logo_wide_dark.svg'; // RAPIDS
import agentLogo from '../assets/images/app_logo_agent.svg';   // AGENT
import llLogo from '../assets/images/orgs/logo-LincolnLaboratory.png';

const useStyles = makeStyles()({
  pageFooter: {
    backgroundColor: '#003E65',
    background: `url(${backgroundImage})`,
    margin: '0 0px',
    padding: 'calc(28px + 2%) 1% 20px 8%',
    border: '0px',
    maxWidth: '100%',
    minHeight: '150px',
    color: 'white',
    fontSize: '14px',
  },
  footerInfo: {
    margin: '0 40px 60px 0',
  },
  appLogo: {
    marginBottom: '12px',
    '& a': {
      paddingLeft: 0,
      width: '100%',
    },
    '& img': {
      width: '45%',
      height: 'auto',
      maxWidth: '300px',
      minWidth: '200px',
      maxHeight: '100px', 
      marginBottom: '15px'
    },
  },
  appDescription: {
    paddingTop: '12px',
    maxWidth: '860px'
  },
  lincolnLabLogo: {
    margin: '55px 0 55px -15px',
    '& a': {
      paddingLeft: 0,
      width: '100%',
    },
    '& img': {
      width: '90%',
      height: 'auto',
      maxWidth: '385px',
      maxHeight: '40px', 
    },
  },
  buildInfo: {
    display: 'flex',
    color: 'lightGrey', 
    padding: '10px 0', 
    fontSize: 'calc(11px + 0.5vmin)',
    '& *': {
      paddingLeft: 0,
    },
  },
  footerLinks: {
    maxWidth: '400px',
    marginRight: '20px',
    marginBottom: '20px',
    '& div': {
      margin: '0 0 12px 0',
      minWidth: '110px',
    },
    '& a, button': {
      color: '#FFAF13',
      padding: 0,
      fontSize: 'calc(11px + 0.5vmin)',
      //fontWeight: 'normal',
    },
    '& a:hover, button:hover': {
      color: '#ffca61',
      textDecoration: 'underline',
    },
  },
  firstLinks: {
    maxWidth: '220px !important',
    minWidth: '170px',
  },
  lastLinks: {
    maxWidth: '220px !important',
    marginRight: '0',
    '& div': {
      minWidth: '224px',
    }
  },
  centerStatement: {
    display: 'flex',
    justifyContent: 'center',
    padding: 5
  },
  ...classificationStyles()
});

function PageFooter({ logged_in, APIBuildInfo, pageInfo, can_create_study, can_create_model, ...props }) {
  const { classes } = useStyles();
    let navigate = useNavigate();
  
  let branch = window._env_.APP_BRANCH || '';
  let build = window._env_.APP_BUILD || '';
  let build_date = window._env_.APP_BUILD_TIMESTAMP || '';
  let UIBuildInfo = branch ? (branch + ' #' + build + ' (' + build_date + ')') : 'Local build';

  return (
    <div value={{ size: '1.4em', style: {marginTop: '-1px', marginRight: '8px'} }}>
      <div className={classes.pageFooter + ' page-footer'}>
        <Grid container>
          <Grid size={{xs:12, lg:6}} className={classes.footerInfo}>
            <Grid className={classes.appLogo}>
              <LinkRouter to='/'>
                {(window._env_?.CUI_APP) &&
                    <img src={agentLogo} alt='app-footer' className='d-inline-block align-top'/>
                }
                {(!window._env_?.CUI_APP) &&
                    <img src={appLogo} alt='app-footer' className='d-inline-block align-top'/>
                }
              </LinkRouter>
            </Grid>
            <Grid>
              <Typography variant='h5' color='white'><b>{window._env_.APP_TAG_LINE}</b></Typography>
            </Grid>
            <Grid className={classes.appDescription}>
              <Typography>
                {window._env_.APP_NAME} is a platform that enables researchers to store and share 
                curated, de-identified data, and is working towards storing and sharing models, results, 
                and analyses. {window._env_.APP_NAME} is being developed by MIT Lincoln Laboratory as a 
                research effort with funding from the National Institutes of Health (NIH), the Defense 
                Threat Reduction Agency (DTRA), and the Biomedical Advanced Research and Development 
                Authority (BARDA).
              </Typography>
            </Grid>
          </Grid>
          
          <Grid size={{xs:12, lg:1}} className={classes.footerLinks + ' ' + classes.firstLinks}>
            <Typography variant='h5' color='white'><b>Pages</b></Typography>
            <div>
              <Button variant='text' onClick={() => navigate('/guide')}>About</Button>
            </div>
            <div>
              <Button variant='text' onClick={() => navigate('/studies')}>Studies</Button>
            </div>
            {(pageInfo?.feature_analyst_workbench) &&
              <div>
                <Button variant='text' onClick={() => navigate('/algorithms')}>Models</Button>
              </div>
            }
            {(pageInfo?.feature_cohort) &&
              <div>
                <Button variant='text' onClick={() => navigate('/cohorts')}>Cohorts</Button>
              </div>
            }
            {(pageInfo?.tools_page) &&
              <div>
                <Button variant='text' onClick={() => navigate('/tools')}>Tools</Button>
              </div>
            }
            {(logged_in && can_create_study) &&
              <div>
                <Button variant='text' onClick={() => navigate('/registerstudy')}>Register a Study</Button>
              </div>
            }
            {(pageInfo?.feature_analyst_workbench && logged_in && can_create_model) &&
              <div>
                <Button variant='text' onClick={() => navigate('/registeralgorithm')}>Register a Model</Button>
              </div>
            }
            {(logged_in) &&
              <div>
                <Button variant='text' onClick={() => navigate('/userprofile')}>User Profile</Button>
              </div>
            }
            {(logged_in) &&
              <div>
                <Button variant='text' onClick={() => navigate('/ethicspledge')}>Ethics Pledge</Button>
              </div>
            }
          </Grid>

          <Grid size={{xs:12, lg:2}} className={classes.footerLinks}>
            <Typography variant='h5' color='white'><b>Sponsor Organizations</b></Typography>
            <div>
              <Button variant='text' href='https://www.nih.gov/' target='_blank' rel='noreferrer'>
                National Institutes of Health
              </Button>
            </div>
            <div>
              <Button variant='text' href='https://www.dtra.mil/' target='_blank' rel='noreferrer'>
                Defense Threat Reduction Agency
              </Button>
            </div>
            <div>
              <Button variant='text' href='https://www.phe.gov/about/barda/Pages/default.aspx' target='_blank' rel='noreferrer'>
                Biomedical Advanced Research and Development Authority
              </Button>
            </div>
          </Grid>

          <Grid size={{xs:12, lg:2}} className={classes.footerLinks + ' ' + classes.lastLinks}>
            <Typography variant='h5' color='white'><b>Developer</b></Typography>
            <div style={{ marginBottom: '30px' }}>
              <Button variant='text' href='https://www.ll.mit.edu/' target='_blank' rel='noreferrer'>
                MIT Lincoln Laboratory
              </Button>
            </div>
            <Typography variant='h5' color='white'><b>Support</b></Typography>
            <div>
              <Button variant='text' href={'mailto:' + window._env_.APP_SUPPORT_EMAIL + '?subject=Support Request'}>
                {window._env_.APP_SUPPORT_EMAIL}
              </Button>
            </div>
          </Grid>
        </Grid>

        <div className={classes.lincolnLabLogo}>
          <a href='https://www.ll.mit.edu/' target='_blank' rel='noreferrer'>
            <img src={llLogo} alt='lincoln-laboratory' className='d-inline-block align-top' />
          </a>
        </div>

        {window._env_.APP_PRODUCTION !== 'true' &&
          <div className={classes.buildInfo}>
            <Typography>UI: {UIBuildInfo}</Typography>
            <br />
            <br />
            <Typography>API: {APIBuildInfo}</Typography>
          </div>
        }
      </div>

      <CuiBanner />
      <CuiBanner stickyBottom={true} />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    APIBuildInfo: state.buildInfo,
    pageInfo: state.pageInfo,
    logged_in: state.userInfo?.logged_in,
    email_verified: state.userInfo?.email_verified,
    can_create_study: state.userInfo.can_create_study,
    can_create_model: state.userInfo.can_create_model
  }
}

export default connect(mapStateToProps)(PageFooter);
